/* eslint-disable max-lines */
export const DATASET_ENTITY_TYPES = [
    "tokens",
    "tokenThemes",
    "figmaComponents",
    "designComponents",
    "assets",
    "documentation",
    "contexts",
    "skills",
    "files",
    "codeComponents",
];
// Workspace-scoped entities. Same filter engine as DS-scoped, but default to
// "include nothing" when no selection is configured (DS-scoped default to all).
export const DATASET_WORKSPACE_ENTITY_TYPES = [
    "contexts",
    "skills",
    "files",
];
const EMPTY_SELECTIONS = {};
const getObjectPropertyValue = (item, property) => {
    return property.split(".").reduce((currentValue, pathSegment) => {
        if (typeof currentValue === "object" &&
            currentValue !== null &&
            pathSegment in currentValue) {
            return currentValue[pathSegment];
        }
        return undefined;
    }, item);
};
const isWorkspaceEntityType = (entityType) => {
    return DATASET_WORKSPACE_ENTITY_TYPES.includes(entityType);
};
const getDefaultPropertyValue = (item, property) => {
    return getObjectPropertyValue(item, property);
};
const normalizeStringValue = (value) => {
    return typeof value === "string" && value.length > 0 ? value : undefined;
};
const normalizePathValue = (value) => {
    if (typeof value === "string" && value.length > 0) {
        return value;
    }
    if (Array.isArray(value)) {
        return value
            .filter((pathSegment) => typeof pathSegment === "string")
            .join("/");
    }
    return undefined;
};
/**
 * Canonical id for dataset filter matching. For `documentation` items, prefer
 * `persistentId` when present — page entities expose both `id` (numeric,
 * version-specific) and `persistentId` (UUID, stable across versions), and
 * filters store the persistentId. Other entity types fall through to `id`,
 * which the SDK already normalizes to the persistent form on construction.
 */
export const getDatasetFilterEntityId = (item, entityType) => {
    if (entityType === "documentation") {
        const { persistentId } = item;
        if (persistentId)
            return persistentId;
    }
    return item.id;
};
const createDatasetFilterEntity = (item, entityType) => {
    var _a, _b;
    return {
        id: getDatasetFilterEntityId(item, entityType),
        name: normalizeStringValue(getObjectPropertyValue(item, "name")),
        path: (_b = (_a = normalizePathValue(getObjectPropertyValue(item, "path"))) !== null && _a !== void 0 ? _a : normalizePathValue(getObjectPropertyValue(item, "tokenPath"))) !== null && _b !== void 0 ? _b : normalizePathValue(getObjectPropertyValue(item, "fullPath")),
        item,
    };
};
const getDatasetFilterEntityPropertyValue = (entity, property) => {
    if (property === "id") {
        return entity.id;
    }
    if (property === "name") {
        return entity.name;
    }
    if (property === "path") {
        return entity.path;
    }
    return getObjectPropertyValue(entity.item, property);
};
const isEmptyValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return true;
    }
    return Array.isArray(value) && value.length === 0;
};
const getFilterStringValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean") {
        return String(value);
    }
    return "";
};
const areValuesEqual = (left, right) => {
    if (typeof left === "string" && typeof right === "string") {
        return left.toLowerCase() === right.toLowerCase();
    }
    if (Array.isArray(left) && Array.isArray(right)) {
        return (left.length === right.length &&
            left.every((value, index) => Object.is(value, right[index])));
    }
    return Object.is(left, right);
};
const containsValue = (value, target) => {
    if (Array.isArray(value)) {
        return value.some((item) => areValuesEqual(item, target));
    }
    return getFilterStringValue(value)
        .toLowerCase()
        .includes(getFilterStringValue(target).toLowerCase());
};
const isInValue = (value, target) => {
    if (!Array.isArray(target)) {
        return false;
    }
    const targets = target;
    if (Array.isArray(value)) {
        return value.some((item) => targets.some((targetItem) => areValuesEqual(item, targetItem)));
    }
    return targets.some((targetItem) => areValuesEqual(value, targetItem));
};
const compareNumberValues = (value, target, comparator) => {
    if (typeof value !== "number" || typeof target !== "number") {
        return false;
    }
    return comparator(value, target);
};
const evaluatePropertyCondition = (item, condition, getPropertyValue) => {
    const value = getPropertyValue(item, condition.property);
    const target = condition.value;
    switch (condition.operator) {
        case "is":
            return areValuesEqual(value, target);
        case "isNot":
            return !areValuesEqual(value, target);
        case "contains":
            return containsValue(value, target);
        case "doesNotContain":
            return !containsValue(value, target);
        case "startsWith":
            return getFilterStringValue(value)
                .toLowerCase()
                .startsWith(getFilterStringValue(target).toLowerCase());
        case "endsWith":
            return getFilterStringValue(value)
                .toLowerCase()
                .endsWith(getFilterStringValue(target).toLowerCase());
        case "isEmpty":
            return isEmptyValue(value);
        case "isNotEmpty":
            return !isEmptyValue(value);
        case "greaterThan":
            return compareNumberValues(value, target, (left, right) => left > right);
        case "greaterThanOrEqual":
            return compareNumberValues(value, target, (left, right) => left >= right);
        case "lessThan":
            return compareNumberValues(value, target, (left, right) => left < right);
        case "lessThanOrEqual":
            return compareNumberValues(value, target, (left, right) => left <= right);
        case "in":
            return isInValue(value, target);
        case "notIn":
            return !isInValue(value, target);
        default:
            return false;
    }
};
const evaluateCondition = (item, condition, getPropertyValue) => {
    if (condition.type === "includeAll") {
        return true;
    }
    if (condition.type === "includeIds") {
        return condition.ids.includes(item.id);
    }
    if (condition.type === "excludeIds") {
        return !condition.ids.includes(item.id);
    }
    return evaluatePropertyCondition(item, condition, getPropertyValue);
};
const evaluateDatasetFilter = (item, filter, getPropertyValue = getDefaultPropertyValue) => {
    if (filter.type !== "group") {
        return evaluateCondition(item, filter, getPropertyValue);
    }
    const results = filter.children.map((child) => evaluateDatasetFilter(item, child, getPropertyValue));
    if (filter.operator === "and") {
        return results.every(Boolean);
    }
    return results.some(Boolean);
};
const mergeGroupsAsUnion = (left, right) => {
    if (!left) {
        return right;
    }
    if (!right) {
        return left;
    }
    return {
        type: "group",
        operator: "or",
        children: [left, right],
    };
};
const pruneFilterNode = (node, shouldKeepCondition) => {
    if (node.type !== "group") {
        return shouldKeepCondition(node) ? node : undefined;
    }
    const children = node.children
        .map((child) => pruneFilterNode(child, shouldKeepCondition))
        .filter((child) => Boolean(child));
    if (!children.length) {
        return undefined;
    }
    return { ...node, children };
};
const collectExcludeIds = (node) => {
    if (!node) {
        return [];
    }
    if (node.type === "excludeIds") {
        return [...node.ids];
    }
    if (node.type !== "group") {
        return [];
    }
    return node.children.flatMap(collectExcludeIds);
};
const createUnionNode = (left, right) => {
    if (!left) {
        return right;
    }
    if (!right) {
        return left;
    }
    return {
        type: "group",
        operator: "or",
        children: [left, right],
    };
};
const createGroupFromNode = (node) => {
    if (!node) {
        return undefined;
    }
    if (node.type === "group") {
        return node;
    }
    return {
        type: "group",
        operator: "and",
        children: [node],
    };
};
const createPositiveSelectionGroup = (refinementGroup) => {
    if (!refinementGroup) {
        return undefined;
    }
    return createGroupFromNode(pruneFilterNode(refinementGroup, (condition) => condition.type !== "excludeIds"));
};
const createRefinedGroup = (baseGroup, refinementGroup) => {
    if (!refinementGroup) {
        return baseGroup;
    }
    const childPositiveNode = createPositiveSelectionGroup(refinementGroup);
    const childPropertyNode = pruneFilterNode(refinementGroup, (condition) => condition.type === "property");
    const excludedIds = [...new Set(collectExcludeIds(refinementGroup))];
    const baseOrChildPositive = createUnionNode(baseGroup, childPositiveNode);
    const children = [];
    if (baseOrChildPositive) {
        children.push(baseOrChildPositive);
    }
    if (childPropertyNode) {
        children.push(childPropertyNode);
    }
    if (excludedIds.length > 0) {
        children.push({ type: "excludeIds", ids: excludedIds });
    }
    if (!children.length) {
        return undefined;
    }
    return {
        type: "group",
        operator: "and",
        children,
    };
};
export class DatasetSelectionBuilder {
    and(children) {
        return {
            type: "group",
            operator: "and",
            children,
        };
    }
    or(children) {
        return {
            type: "group",
            operator: "or",
            children,
        };
    }
    includeAll() {
        return { type: "includeAll" };
    }
    includeIds(ids) {
        return { type: "includeIds", ids };
    }
    excludeIds(ids) {
        return { type: "excludeIds", ids };
    }
    where(property, operator, value) {
        return {
            type: "property",
            property,
            operator,
            value,
        };
    }
}
function walkFilterConditions(node, visit) {
    if (node.type === "group") {
        node.children.forEach((child) => walkFilterConditions(child, visit));
        return;
    }
    visit(node);
}
function collectIdsFromGroup(group, kind) {
    const ids = new Set();
    walkFilterConditions(group, (condition) => {
        if (condition.type === kind) {
            condition.ids.forEach((id) => ids.add(id));
        }
    });
    return ids;
}
function groupHasIncludeAll(group) {
    let found = false;
    walkFilterConditions(group, (condition) => {
        if (condition.type === "includeAll")
            found = true;
    });
    return found;
}
export class SDKDataset {
    constructor(data = {}, refinedSourceDataset, refinedBaseDataset) {
        var _a, _b;
        const selections = (_a = data.selections) !== null && _a !== void 0 ? _a : EMPTY_SELECTIONS;
        this.designSystemId = (_b = data.designSystemId) !== null && _b !== void 0 ? _b : null;
        this.tokens = selections.tokens;
        this.tokenThemes = selections.tokenThemes;
        this.figmaComponents = selections.figmaComponents;
        this.designComponents = selections.designComponents;
        this.assets = selections.assets;
        this.documentation = selections.documentation;
        this.contexts = selections.contexts;
        this.skills = selections.skills;
        this.files = selections.files;
        this.codeComponents = selections.codeComponents;
        this.refinedSourceDataset = refinedSourceDataset;
        this.refinedBaseDataset = refinedBaseDataset;
    }
    static create(designSystemId = null) {
        return new SDKDataset({ designSystemId });
    }
    static and(children) {
        return {
            type: "group",
            operator: "and",
            children,
        };
    }
    static or(children) {
        return {
            type: "group",
            operator: "or",
            children,
        };
    }
    static includeAll() {
        return { type: "includeAll" };
    }
    static includeIds(ids) {
        return { type: "includeIds", ids };
    }
    static excludeIds(ids) {
        return { type: "excludeIds", ids };
    }
    withSelection(entityType, buildGroup) {
        const group = buildGroup(new DatasetSelectionBuilder());
        return new SDKDataset({
            designSystemId: this.designSystemId,
            selections: {
                ...this.getSelections(),
                [entityType]: group,
            },
        });
    }
    withoutSelection(entityType) {
        const selections = { ...this.getSelections() };
        delete selections[entityType];
        return new SDKDataset({
            designSystemId: this.designSystemId,
            selections,
        });
    }
    canUnionWith(other) {
        return (this.designSystemId === other.designSystemId ||
            this.designSystemId === null ||
            other.designSystemId === null);
    }
    union(other) {
        var _a;
        if (!this.canUnionWith(other)) {
            throw new Error(`Cannot union datasets with incompatible design systems: ${String(this.designSystemId)} and ${String(other.designSystemId)}`);
        }
        const selections = DATASET_ENTITY_TYPES.reduce((currentSelections, entityType) => {
            const group = mergeGroupsAsUnion(this[entityType], other[entityType]);
            if (group) {
                currentSelections[entityType] = group;
            }
            return currentSelections;
        }, {});
        return new SDKDataset({
            designSystemId: (_a = this.designSystemId) !== null && _a !== void 0 ? _a : other.designSystemId,
            selections,
        });
    }
    refine(baseDataset) {
        var _a;
        if (!this.canUnionWith(baseDataset)) {
            throw new Error(`Cannot refine datasets with incompatible design systems: ${String(this.designSystemId)} and ${String(baseDataset.designSystemId)}`);
        }
        const selections = DATASET_ENTITY_TYPES.reduce((currentSelections, entityType) => {
            const group = createRefinedGroup(baseDataset[entityType], this[entityType]);
            if (group) {
                currentSelections[entityType] = group;
            }
            return currentSelections;
        }, {});
        const positiveSelections = DATASET_ENTITY_TYPES.reduce((currentSelections, entityType) => {
            const group = createPositiveSelectionGroup(this[entityType]);
            if (group) {
                currentSelections[entityType] = group;
            }
            return currentSelections;
        }, {});
        return new SDKDataset({
            designSystemId: (_a = baseDataset.designSystemId) !== null && _a !== void 0 ? _a : this.designSystemId,
            selections,
        }, new SDKDataset({
            designSystemId: this.designSystemId,
            selections: positiveSelections,
        }), baseDataset);
    }
    resolve(repository, getPropertyValue) {
        return DATASET_ENTITY_TYPES.reduce((result, entityType) => {
            const group = this[entityType];
            if (!group) {
                return result;
            }
            const designSystemId = isWorkspaceEntityType(entityType)
                ? null
                : this.designSystemId;
            const items = repository.getAllForType(entityType, designSystemId);
            const matchingItems = items.filter((item) => evaluateDatasetFilter(item, group, getPropertyValue));
            const filteredItems = this.mapItemsToResults(matchingItems, "current");
            if (filteredItems.length) {
                result[entityType] = filteredItems;
            }
            return result;
        }, {});
    }
    filteredTokens(allTokens, inheritedDataset) {
        return this.filteredItems("tokens", allTokens, inheritedDataset);
    }
    filteredTokenThemes(allTokenThemes, inheritedDataset) {
        return this.filteredItems("tokenThemes", allTokenThemes, inheritedDataset);
    }
    filteredFigmaComponents(allFigmaComponents, inheritedDataset) {
        return this.filteredItems("figmaComponents", allFigmaComponents, inheritedDataset);
    }
    filteredDesignComponents(allDesignComponents, inheritedDataset) {
        return this.filteredItems("designComponents", allDesignComponents, inheritedDataset);
    }
    filteredAssets(allAssets, inheritedDataset) {
        return this.filteredItems("assets", allAssets, inheritedDataset);
    }
    filteredDocumentation(allDocumentation, inheritedDataset) {
        return this.filteredItems("documentation", allDocumentation, inheritedDataset);
    }
    filteredContexts(allContexts, inheritedDataset) {
        return this.filteredItems("contexts", allContexts, inheritedDataset);
    }
    filteredSkills(allSkills, inheritedDataset) {
        return this.filteredItems("skills", allSkills, inheritedDataset);
    }
    filteredFiles(allFiles, inheritedDataset) {
        return this.filteredItems("files", allFiles, inheritedDataset);
    }
    filteredCodeComponents(allCodeComponents, inheritedDataset) {
        return this.filteredItems("codeComponents", allCodeComponents, inheritedDataset);
    }
    filteredItems(entityType, allItems, inheritedDataset, projectItem = (item) => createDatasetFilterEntity(item, entityType)) {
        var _a;
        const currentIds = this.getMatchingItemIds(entityType, allItems, projectItem);
        const refinedCurrentIds = !inheritedDataset && this.refinedSourceDataset
            ? this.refinedSourceDataset.getMatchingItemIds(entityType, allItems, projectItem)
            : undefined;
        const refinedInheritedIds = !inheritedDataset && this.refinedBaseDataset
            ? this.refinedBaseDataset.getMatchingItemIds(entityType, allItems, projectItem)
            : undefined;
        const inheritedIds = (_a = inheritedDataset === null || inheritedDataset === void 0 ? void 0 : inheritedDataset.getMatchingItemIds(entityType, allItems, projectItem)) !== null && _a !== void 0 ? _a : new Set();
        return allItems.reduce((results, item) => {
            const projectedItem = projectItem(item);
            if (refinedCurrentIds || refinedInheritedIds) {
                if (!currentIds.has(projectedItem.id)) {
                    return results;
                }
                const source = (refinedCurrentIds === null || refinedCurrentIds === void 0 ? void 0 : refinedCurrentIds.has(projectedItem.id)) ||
                    !(refinedInheritedIds === null || refinedInheritedIds === void 0 ? void 0 : refinedInheritedIds.has(projectedItem.id))
                    ? "current"
                    : "inherited";
                results.push({
                    item,
                    source,
                });
                return results;
            }
            if (currentIds.has(projectedItem.id)) {
                results.push({ item, source: "current" });
                return results;
            }
            if (inheritedIds.has(projectedItem.id)) {
                results.push({ item, source: "inherited" });
            }
            return results;
        }, []);
    }
    /**
     * Id-only membership check against the dataset's scope for one entity type.
     * Walks the filter tree for `includeAll`, `includeIds`, `excludeIds` and
     * returns a structured reason. Does not evaluate property predicates — for
     * those use `filteredItems`.
     *
     * Default for a missing filter group: workspace-scoped entities drop;
     * DS-scoped entities pass. See `DATASET_WORKSPACE_ENTITY_TYPES` for why.
     */
    isIdInScope(entityType, id) {
        const group = this[entityType];
        if (!group) {
            return isWorkspaceEntityType(entityType)
                ? { status: "not_in_scope", reason: "not_in_includes" }
                : { status: "in_scope", reason: "no_filter_configured" };
        }
        const excludeIds = collectIdsFromGroup(group, "excludeIds");
        if (excludeIds.has(id)) {
            return { status: "not_in_scope", reason: "explicit_exclude" };
        }
        if (groupHasIncludeAll(group)) {
            return { status: "in_scope", reason: "includeAll" };
        }
        const includeIds = collectIdsFromGroup(group, "includeIds");
        if (includeIds.has(id)) {
            return { status: "in_scope", reason: "explicit_include" };
        }
        return { status: "not_in_scope", reason: "not_in_includes" };
    }
    isEmpty() {
        return DATASET_ENTITY_TYPES.every((entityType) => !this[entityType]);
    }
    getSelections() {
        return DATASET_ENTITY_TYPES.reduce((selections, entityType) => {
            const group = this[entityType];
            if (group) {
                selections[entityType] = group;
            }
            return selections;
        }, {});
    }
    toRemote() {
        return {
            designSystemId: this.designSystemId,
            selections: this.getSelections(),
        };
    }
    getMatchingItemIds(entityType, allItems, projectItem) {
        const group = this[entityType];
        if (!group) {
            // Default for a missing filter group — see `DATASET_WORKSPACE_ENTITY_TYPES`.
            if (isWorkspaceEntityType(entityType)) {
                return new Set();
            }
            return new Set(allItems.map((item) => projectItem(item).id));
        }
        return allItems.reduce((ids, item) => {
            const entity = projectItem(item);
            if (evaluateDatasetFilter(entity, group, getDatasetFilterEntityPropertyValue)) {
                ids.add(entity.id);
            }
            return ids;
        }, new Set());
    }
    mapItemsToResults(items, source) {
        const seenIds = new Set();
        return items.reduce((results, item) => {
            if (seenIds.has(item.id)) {
                return results;
            }
            seenIds.add(item.id);
            results.push({ item, source });
            return results;
        }, []);
    }
}
